/**
 * AI Tools for Projects and Tasks
 *
 * Tools that allow Marta to interact with projects, tasks,
 * milestones, and time entries via GraphQL.
 */

import type { ToolDefinition } from './types';
import {
  graphqlQuery,
  graphqlMutation,
  PROJECT_FIELDS,
  TASK_FIELDS,
  MILESTONE_FIELDS,
} from '../../database/graphqlClient';

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const QUERIES = {
  projects: `query ListProjects { projects { ${PROJECT_FIELDS} } }`,
  project: `query GetProject($id: ID!) { project(id: $id) { ${PROJECT_FIELDS} } }`,
  tasks: `query ListTasks($projectId: ID) { tasks(projectId: $projectId) { ${TASK_FIELDS} } }`,
  task: `query GetTask($id: ID!) { task(id: $id) { ${TASK_FIELDS} } }`,
  milestones: `query ListMilestones($projectId: ID) { milestones(projectId: $projectId) { ${MILESTONE_FIELDS} } }`,
};

const MUTATIONS = {
  createProject: `mutation CreateProject($input: ProjectInput!) { createProject(input: $input) { ${PROJECT_FIELDS} } }`,
  updateProject: `mutation UpdateProject($id: ID!, $input: ProjectInput!) { updateProject(id: $id, input: $input) { ${PROJECT_FIELDS} } }`,
  deleteProject: `mutation DeleteProject($id: ID!) { deleteProject(id: $id) }`,
  createTask: `mutation CreateTask($input: TaskInput!) { createTask(input: $input) { ${TASK_FIELDS} } }`,
  updateTask: `mutation UpdateTask($id: ID!, $input: TaskInput!) { updateTask(id: $id, input: $input) { ${TASK_FIELDS} } }`,
  deleteTask: `mutation DeleteTask($id: ID!) { deleteTask(id: $id) }`,
  createMilestone: `mutation CreateMilestone($input: MilestoneInput!) { createMilestone(input: $input) { ${MILESTONE_FIELDS} } }`,
  deleteMilestone: `mutation DeleteMilestone($id: ID!) { deleteMilestone(id: $id) }`,
};

// =============================================================================
// TOOL DEFINITIONS
// =============================================================================

export const searchProjectsTool: ToolDefinition = {
  name: 'search_projects',
  description: 'Cerca progetti esistenti. Può filtrare per stato (planning, active, on_hold, completed, cancelled) o nome.',
  category: 'projects',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.projects,
  parameters: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        description: 'Filtra per stato del progetto',
        enum: ['planning', 'active', 'on_hold', 'completed', 'cancelled'],
      },
      searchTerm: {
        type: 'string',
        description: 'Termine di ricerca per filtrare per nome/titolo',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 10)',
      },
    },
  },
};

export const getProjectTool: ToolDefinition = {
  name: 'get_project',
  description: 'Ottiene i dettagli di un progetto specifico dato il suo ID.',
  category: 'projects',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.project,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del progetto',
      },
    },
    required: ['id'],
  },
};

export const createProjectTool: ToolDefinition = {
  name: 'create_project',
  description: 'Crea un nuovo progetto. Richiede almeno titolo e data di inizio.',
  category: 'projects',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createProject,
  parameters: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Titolo del progetto',
      },
      description: {
        type: 'string',
        description: 'Descrizione del progetto',
      },
      status: {
        type: 'string',
        description: 'Stato iniziale del progetto',
        enum: ['planning', 'active', 'on_hold', 'completed', 'cancelled'],
        default: 'planning',
      },
      priority: {
        type: 'string',
        description: 'Priorità del progetto',
        enum: ['bassa', 'media', 'alta', 'urgente'],
        default: 'media',
      },
      startDate: {
        type: 'string',
        description: 'Data di inizio (formato YYYY-MM-DD)',
      },
      dueAt: {
        type: 'string',
        description: 'Data di scadenza (formato YYYY-MM-DD)',
      },
      budgetTotal: {
        type: 'number',
        description: 'Budget totale del progetto',
      },
      clientName: {
        type: 'string',
        description: 'Nome del cliente',
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tag del progetto',
      },
    },
    required: ['title', 'startDate'],
  },
};

export const updateProjectTool: ToolDefinition = {
  name: 'update_project',
  description: 'Modifica un progetto esistente. Specifica l\'ID e i campi da aggiornare.',
  category: 'projects',
  operation: 'update',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.updateProject,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del progetto da modificare',
      },
      title: {
        type: 'string',
        description: 'Nuovo titolo',
      },
      description: {
        type: 'string',
        description: 'Nuova descrizione',
      },
      status: {
        type: 'string',
        description: 'Nuovo stato',
        enum: ['planning', 'active', 'on_hold', 'completed', 'cancelled'],
      },
      priority: {
        type: 'string',
        description: 'Nuova priorità',
        enum: ['bassa', 'media', 'alta', 'urgente'],
      },
      progress: {
        type: 'number',
        description: 'Percentuale di completamento (0-100)',
      },
      dueAt: {
        type: 'string',
        description: 'Nuova data di scadenza',
      },
      favorite: {
        type: 'boolean',
        description: 'Segna come preferito',
      },
    },
    required: ['id'],
  },
};

export const deleteProjectTool: ToolDefinition = {
  name: 'delete_project',
  description: 'Elimina un progetto. ATTENZIONE: Questa azione è irreversibile e eliminerà anche tutti i task associati.',
  category: 'projects',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteProject,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del progetto da eliminare',
      },
      projectName: {
        type: 'string',
        description: 'Nome del progetto (per conferma)',
      },
    },
    required: ['id'],
  },
};

// =============================================================================
// TASK TOOLS
// =============================================================================

export const searchTasksTool: ToolDefinition = {
  name: 'search_tasks',
  description: 'Cerca task. Può filtrare per progetto, stato o termine di ricerca.',
  category: 'tasks',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.tasks,
  parameters: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'ID del progetto per filtrare i task',
      },
      status: {
        type: 'string',
        description: 'Filtra per stato',
        enum: ['todo', 'in_progress', 'review', 'done', 'blocked'],
      },
      searchTerm: {
        type: 'string',
        description: 'Termine di ricerca per titolo/descrizione',
      },
      done: {
        type: 'boolean',
        description: 'Filtra per completamento',
      },
    },
  },
};

export const getTaskTool: ToolDefinition = {
  name: 'get_task',
  description: 'Ottiene i dettagli di un task specifico.',
  category: 'tasks',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.task,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del task',
      },
    },
    required: ['id'],
  },
};

export const createTaskTool: ToolDefinition = {
  name: 'create_task',
  description: 'Crea un nuovo task in un progetto.',
  category: 'tasks',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createTask,
  parameters: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'ID del progetto a cui appartiene il task',
      },
      title: {
        type: 'string',
        description: 'Titolo del task',
      },
      description: {
        type: 'string',
        description: 'Descrizione del task',
      },
      status: {
        type: 'string',
        description: 'Stato iniziale',
        enum: ['todo', 'in_progress', 'review', 'done', 'blocked'],
        default: 'todo',
      },
      priority: {
        type: 'string',
        description: 'Priorità del task',
        enum: ['bassa', 'media', 'alta', 'urgente'],
        default: 'media',
      },
      dueAt: {
        type: 'string',
        description: 'Data di scadenza (formato YYYY-MM-DD)',
      },
      estimatedMinutes: {
        type: 'number',
        description: 'Tempo stimato in minuti',
      },
      assignees: {
        type: 'array',
        items: { type: 'string' },
        description: 'Lista ID degli assegnatari',
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tag del task',
      },
    },
    required: ['projectId', 'title'],
  },
};

export const updateTaskTool: ToolDefinition = {
  name: 'update_task',
  description: 'Modifica un task esistente.',
  category: 'tasks',
  operation: 'update',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.updateTask,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del task da modificare',
      },
      title: {
        type: 'string',
        description: 'Nuovo titolo',
      },
      description: {
        type: 'string',
        description: 'Nuova descrizione',
      },
      status: {
        type: 'string',
        description: 'Nuovo stato',
        enum: ['todo', 'in_progress', 'review', 'done', 'blocked'],
      },
      priority: {
        type: 'string',
        description: 'Nuova priorità',
        enum: ['bassa', 'media', 'alta', 'urgente'],
      },
      done: {
        type: 'boolean',
        description: 'Segna come completato/non completato',
      },
      dueAt: {
        type: 'string',
        description: 'Nuova data di scadenza',
      },
    },
    required: ['id'],
  },
};

export const markTaskDoneTool: ToolDefinition = {
  name: 'mark_task_done',
  description: 'Segna un task come completato o da completare.',
  category: 'tasks',
  operation: 'update',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.updateTask,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del task',
      },
      done: {
        type: 'boolean',
        description: 'true = completato, false = da completare',
        default: true,
      },
    },
    required: ['id'],
  },
};

export const deleteTaskTool: ToolDefinition = {
  name: 'delete_task',
  description: 'Elimina un task. ATTENZIONE: Questa azione è irreversibile.',
  category: 'tasks',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteTask,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del task da eliminare',
      },
      taskName: {
        type: 'string',
        description: 'Nome del task (per conferma)',
      },
    },
    required: ['id'],
  },
};

// =============================================================================
// MILESTONE TOOLS
// =============================================================================

export const listMilestonesTool: ToolDefinition = {
  name: 'list_milestones',
  description: 'Elenca le milestone di un progetto.',
  category: 'projects',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.milestones,
  parameters: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'ID del progetto',
      },
    },
    required: ['projectId'],
  },
};

export const createMilestoneTool: ToolDefinition = {
  name: 'create_milestone',
  description: 'Crea una nuova milestone per un progetto.',
  category: 'projects',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createMilestone,
  parameters: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'ID del progetto',
      },
      title: {
        type: 'string',
        description: 'Titolo della milestone',
      },
      description: {
        type: 'string',
        description: 'Descrizione della milestone',
      },
      dueDate: {
        type: 'string',
        description: 'Data di scadenza (formato YYYY-MM-DD)',
      },
    },
    required: ['projectId', 'title', 'dueDate'],
  },
};

export const deleteMilestoneTool: ToolDefinition = {
  name: 'delete_milestone',
  description: 'Elimina una milestone. ATTENZIONE: Questa azione è irreversibile.',
  category: 'projects',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteMilestone,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della milestone da eliminare',
      },
    },
    required: ['id'],
  },
};

// =============================================================================
// TOOL EXECUTION FUNCTIONS
// =============================================================================

export async function executeSearchProjects(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ projects: unknown[] }>(QUERIES.projects);
  let projects = result.projects || [];

  // Apply filters
  if (params.status) {
    projects = projects.filter((p: { status?: string }) => p.status === params.status);
  }
  if (params.searchTerm) {
    const term = String(params.searchTerm).toLowerCase();
    projects = projects.filter(
      (p: { title?: string; description?: string }) =>
        p.title?.toLowerCase().includes(term) || p.description?.toLowerCase().includes(term)
    );
  }

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 10;
  projects = projects.slice(0, limit);

  return { projects, count: projects.length };
}

export async function executeGetProject(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ project: unknown }>(QUERIES.project, { id: params.id });
  return result.project;
}

export async function executeCreateProject(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    title: params.title,
    description: params.description || '',
    status: params.status || 'planning',
    priority: params.priority || 'media',
    progress: 0,
    startDate: params.startDate,
    dueAt: params.dueAt || null,
    budgetTotal: params.budgetTotal || null,
    risk: 'ok',
    favorite: false,
    clientId: null,
    clientName: params.clientName || null,
    team: [],
    tags: params.tags || [],
  };

  const result = await graphqlMutation<{ createProject: unknown }>(MUTATIONS.createProject, { input });
  return result.createProject;
}

export async function executeUpdateProject(params: Record<string, unknown>): Promise<unknown> {
  // First get current project
  const currentResult = await graphqlQuery<{ project: Record<string, unknown> | null }>(QUERIES.project, {
    id: params.id,
  });

  if (!currentResult.project) {
    throw new Error(`Progetto non trovato: ${params.id}`);
  }

  const current = currentResult.project;
  const input = {
    title: params.title ?? current.title,
    description: params.description ?? current.description,
    status: params.status ?? current.status,
    priority: params.priority ?? current.priority,
    progress: params.progress ?? current.progress,
    startDate: current.startDate,
    dueAt: params.dueAt ?? current.dueAt ?? null,
    budgetTotal: current.budgetTotal ?? null,
    risk: current.risk ?? 'ok',
    favorite: params.favorite ?? current.favorite,
    clientId: current.clientId ?? null,
    clientName: current.clientName ?? null,
    team: current.team ?? [],
    tags: current.tags ?? [],
  };

  const result = await graphqlMutation<{ updateProject: unknown }>(MUTATIONS.updateProject, {
    id: params.id,
    input,
  });
  return result.updateProject;
}

export async function executeDeleteProject(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteProject, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeSearchTasks(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ tasks: unknown[] }>(QUERIES.tasks, {
    projectId: params.projectId || null,
  });
  let tasks = result.tasks || [];

  // Apply filters
  if (params.status) {
    tasks = tasks.filter((t: { status?: string }) => t.status === params.status);
  }
  if (params.done !== undefined) {
    tasks = tasks.filter((t: { done?: boolean }) => t.done === params.done);
  }
  if (params.searchTerm) {
    const term = String(params.searchTerm).toLowerCase();
    tasks = tasks.filter(
      (t: { title?: string; description?: string }) =>
        t.title?.toLowerCase().includes(term) || t.description?.toLowerCase().includes(term)
    );
  }

  return { tasks, count: tasks.length };
}

export async function executeGetTask(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ task: unknown }>(QUERIES.task, { id: params.id });
  return result.task;
}

export async function executeCreateTask(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    projectId: params.projectId,
    parentId: null,
    order: 0,
    title: params.title,
    description: params.description || null,
    status: params.status || 'todo',
    done: false,
    priority: params.priority || 'media',
    startAt: params.startAt || null,
    dueAt: params.dueAt || null,
    estimatedMinutes: params.estimatedMinutes || null,
    loggedMinutes: null,
    assignees: params.assignees || [],
    tags: params.tags || [],
  };

  const result = await graphqlMutation<{ createTask: unknown }>(MUTATIONS.createTask, { input });
  return result.createTask;
}

export async function executeUpdateTask(params: Record<string, unknown>): Promise<unknown> {
  // First get current task
  const currentResult = await graphqlQuery<{ task: Record<string, unknown> | null }>(QUERIES.task, {
    id: params.id,
  });

  if (!currentResult.task) {
    throw new Error(`Task non trovato: ${params.id}`);
  }

  const current = currentResult.task;
  const input = {
    projectId: current.projectId,
    parentId: current.parentId ?? null,
    order: current.order ?? 0,
    title: params.title ?? current.title,
    description: params.description ?? current.description ?? null,
    status: params.status ?? current.status,
    done: params.done ?? current.done,
    priority: params.priority ?? current.priority ?? 'media',
    startAt: current.startAt ?? null,
    dueAt: params.dueAt ?? current.dueAt ?? null,
    estimatedMinutes: current.estimatedMinutes ?? null,
    loggedMinutes: current.loggedMinutes ?? null,
    assignees: current.assignees ?? [],
    tags: current.tags ?? [],
  };

  const result = await graphqlMutation<{ updateTask: unknown }>(MUTATIONS.updateTask, {
    id: params.id,
    input,
  });
  return result.updateTask;
}

export async function executeMarkTaskDone(params: Record<string, unknown>): Promise<unknown> {
  const done = params.done !== false;
  return executeUpdateTask({ id: params.id, done, status: done ? 'done' : 'todo' });
}

export async function executeDeleteTask(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteTask, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeListMilestones(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ milestones: unknown[] }>(QUERIES.milestones, {
    projectId: params.projectId,
  });
  return { milestones: result.milestones || [] };
}

export async function executeCreateMilestone(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    projectId: params.projectId,
    title: params.title,
    description: params.description || null,
    dueDate: params.dueDate,
    completed: false,
  };

  const result = await graphqlMutation<{ createMilestone: unknown }>(MUTATIONS.createMilestone, { input });
  return result.createMilestone;
}

export async function executeDeleteMilestone(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteMilestone, { id: params.id });
  return { deleted: true, id: params.id };
}

// =============================================================================
// EXPORTS
// =============================================================================

export const projectTools: ToolDefinition[] = [
  searchProjectsTool,
  getProjectTool,
  createProjectTool,
  updateProjectTool,
  deleteProjectTool,
  searchTasksTool,
  getTaskTool,
  createTaskTool,
  updateTaskTool,
  markTaskDoneTool,
  deleteTaskTool,
  listMilestonesTool,
  createMilestoneTool,
  deleteMilestoneTool,
];

export const projectToolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  search_projects: executeSearchProjects,
  get_project: executeGetProject,
  create_project: executeCreateProject,
  update_project: executeUpdateProject,
  delete_project: executeDeleteProject,
  search_tasks: executeSearchTasks,
  get_task: executeGetTask,
  create_task: executeCreateTask,
  update_task: executeUpdateTask,
  mark_task_done: executeMarkTaskDone,
  delete_task: executeDeleteTask,
  list_milestones: executeListMilestones,
  create_milestone: executeCreateMilestone,
  delete_milestone: executeDeleteMilestone,
};
